import type { FtwStageKey, StageStatus, StageColumn } from '@/external/db/types';

export type { StageStatus };
export type StageGroup = 'Intake' | 'Computation' | 'Compliance Tests' | 'Output';

export type StageDef = {
  slug: string;
  name: string;
  group: StageGroup;
  number: number;
  eyebrow: string;
  title: string;
  subtitle: string;
  column: StageColumn;
  // Only for stages whose content explains what is missing rather than
  // assuming computed data.
  renderWhenPending?: boolean;
};

export type Stage = StageDef & {
  status: StageStatus;
  meta: string | null;
};

export const stageDefs: StageDef[] = [
  {
    slug: 'payroll',
    name: 'Payroll ingestion',
    group: 'Intake',
    number: 1,
    eyebrow: 'Stage 01 · Intake',
    title: 'Payroll ingestion',
    subtitle:
      'Will import pay period exports from your payroll provider and validate each period against expected schema. Deferral, compensation, and match columns are reconciled at the row level.',
    column: 'stage_payroll',
    renderWhenPending: true,
  },
  {
    slug: 'recordkeeper',
    name: 'Recordkeeper ingestion',
    group: 'Intake',
    number: 2,
    eyebrow: 'Stage 02 · Intake',
    title: 'Recordkeeper ingestion',
    subtitle:
      "Will ingest the recordkeeper's full plan-year extract: per-pay-period deposit ledger, source-tagged contributions, trust statements, distribution history, and the year-end participant roster.",
    column: 'stage_recordkeeper',
    renderWhenPending: true,
  },
  {
    slug: 'census',
    name: 'Census construction',
    group: 'Intake',
    number: 3,
    eyebrow: 'Stage 03 · Intake',
    title: 'Census construction',
    subtitle:
      'Will merge the HRIS census, payroll roster, and recordkeeper participant file into a unified plan-year census. Captures new hires, mid-year terminations, and rehires.',
    column: 'stage_census',
    renderWhenPending: true,
  },
  {
    slug: 'plan-rules',
    name: 'Plan rules parsed',
    group: 'Intake',
    number: 4,
    eyebrow: 'Stage 04 · Intake',
    title: 'Plan rules parsed',
    subtitle:
      'Will parse the adoption agreement and any amendments into structured provisions covering eligibility, vesting, contribution rules, and plan design elections.',
    column: 'stage_plan_rules',
  },
  {
    slug: 'eligibility',
    name: 'Eligibility',
    group: 'Computation',
    number: 5,
    eyebrow: 'Stage 05 · Computation',
    title: 'Eligibility',
    subtitle:
      "Will apply the plan's age and service requirements to determine each participant's eligibility status and plan entry date during the plan year.",
    column: 'stage_eligibility',
    renderWhenPending: true,
  },
  {
    slug: 'compensation',
    name: 'Compensation',
    group: 'Computation',
    number: 6,
    eyebrow: 'Stage 06 · Computation',
    title: 'Compensation',
    subtitle:
      'Will build compensation definitions per plan document: gross W-2, 414(s) safe harbor, and plan compensation. Applies the §401(a)(17) annual limit where applicable.',
    column: 'stage_compensation',
  },
  {
    slug: 'reconciliation',
    name: 'Reconciliation',
    group: 'Computation',
    number: 7,
    eyebrow: 'Stage 07 · Computation',
    title: 'Reconciliation',
    subtitle:
      'Will match payroll-side contributions against the recordkeeper deposit ledger for each pay period. Flags variances and posting-lag exceptions for reviewer action before downstream stages run.',
    column: 'stage_reconciliation',
  },
  {
    slug: 'hce',
    name: 'HCE / Key',
    group: 'Computation',
    number: 8,
    eyebrow: 'Stage 08 · Computation',
    title: 'HCE / Key employee classification',
    subtitle:
      'Will identify highly-compensated and key employees per IRC §414(q) and §416. Applies top-paid group elections and family attribution rules as specified in the plan document.',
    column: 'stage_hce',
  },
  {
    slug: '410b-coverage',
    name: '410(b) coverage',
    group: 'Compliance Tests',
    number: 9,
    eyebrow: 'Stage 09 · Compliance Tests',
    title: '410(b) coverage',
    subtitle:
      'Will run the ratio percentage test for each contribution source after HCE classification finalizes. Ensures the plan benefits a non-discriminatory cross-section of the workforce.',
    column: 'stage_coverage_410b',
  },
  {
    slug: 'adp-acp',
    name: 'ADP / ACP',
    group: 'Compliance Tests',
    number: 10,
    eyebrow: 'Stage 10 · Compliance Tests',
    title: 'ADP / ACP nondiscrimination',
    subtitle:
      'Will run actual deferral and contribution percentage tests. Riverside elected current-year testing method. Plan is not a safe harbor, so corrections may be required if HCE rate exceeds limits.',
    column: 'stage_adp_acp',
  },
  {
    slug: '402g-limits',
    name: '402(g) limits',
    group: 'Compliance Tests',
    number: 11,
    eyebrow: 'Stage 11 · Compliance Tests',
    title: '402(g) elective deferral limits',
    subtitle:
      "Will check each participant's elective deferrals against the $23,500 (under 50) / $31,000 (50+) limits for 2025. Any excess must be distributed by 04.15.2026.",
    column: 'stage_limits_402g',
  },
  {
    slug: '415-additions',
    name: '415 additions',
    group: 'Compliance Tests',
    number: 12,
    eyebrow: 'Stage 12 · Compliance Tests',
    title: '415 annual additions',
    subtitle:
      'Will validate that combined deferrals + match + employer contributions for each participant remain under the $70,000 / 100% of compensation limit for 2025.',
    column: 'stage_additions_415',
  },
  {
    slug: 'top-heavy',
    name: 'Top-heavy',
    group: 'Compliance Tests',
    number: 13,
    eyebrow: 'Stage 13 · Compliance Tests',
    title: 'Top-heavy determination',
    subtitle:
      'Will compute the ratio of key-employee account balances to total plan assets at the determination date (12.31.2024). If > 60%, top-heavy minimum contribution applies for non-key participants.',
    column: 'stage_top_heavy',
  },
  {
    slug: 'corrections',
    name: 'Corrections',
    group: 'Output',
    number: 14,
    eyebrow: 'Stage 14 · Output',
    title: 'Corrections & refunds',
    subtitle:
      'Will assemble the action list for any failed tests: corrective distributions, QNEC contributions, lost-earnings calculations, and any VFCP / SCP filings the recordkeeper needs to process.',
    column: 'stage_corrections',
  },
  {
    slug: 'trust',
    name: 'Trust accounting',
    group: 'Output',
    number: 15,
    eyebrow: 'Stage 15 · Output',
    title: 'Trust accounting',
    subtitle:
      'Will build the trust-level activity rollforward: beginning balance, contributions, earnings, fees, distributions, ending balance. Sourced from Empower trust statements and reconciled to the recordkeeper file.',
    column: 'stage_trust',
  },
  {
    slug: 'participant-counts',
    name: 'Participant counts',
    group: 'Output',
    number: 16,
    eyebrow: 'Stage 16 · Output',
    title: 'Participant counts',
    subtitle:
      'Will compute the BOY / EOY participant counts for Form 5500 lines 5–6: active, retired/separated with balance, beneficiaries, deceased. Drives the small-plan vs large-plan filing threshold.',
    column: 'stage_participant_counts',
  },
  {
    slug: 'form-assembly',
    name: 'Form assembly',
    group: 'Output',
    number: 17,
    eyebrow: 'Stage 17 · Output',
    title: 'Form 5500-SF assembly',
    subtitle:
      'Will assemble the Form 5500-SF line-by-line with full source traceability. Each line item will link back to the underlying computation, source file, and reviewer sign-off.',
    column: 'stage_form_assembly',
  },
  {
    slug: 'filing',
    name: 'EFAST2 submission',
    group: 'Output',
    number: 18,
    eyebrow: 'Stage 18 · Output',
    title: 'EFAST2 submission',
    subtitle:
      "Will submit the signed Form 5500-SF package to the DOL's EFAST2 system. Routes plan administrator and sponsor signatures through DocuSign, captures the EFAST2 acknowledgement ID, and archives the filing receipt.",
    column: 'stage_filing',
  },
];

export const stageGroups: StageGroup[] = ['Intake', 'Computation', 'Compliance Tests', 'Output'];

export function getStageDef(slug: string): StageDef | undefined {
  return stageDefs.find(s => s.slug === slug);
}

export function statusLabel(status: StageStatus): string {
  if (status === 'done') {
    return 'Complete';
  } else if (status === 'running') {
    return 'Running';
  } else if (status === 'review') {
    return 'Needs review';
  } else if (status === 'blocked') {
    return 'Blocked';
  } else if (status === 'pending') {
    return 'Pending';
  } else {
    const _exhaustive: never = status;
    throw new Error(`unhandled: ${String(_exhaustive)}`);
  }
}

const FTW_LABEL_TO_SLUG: Record<string, string> = {
  '410(b) coverage': '410b-coverage',
  'ADP/ACP': 'adp-acp',
  '402(g) deferral limit': '402g-limits',
  '415 annual additions': '415-additions',
  'top-heavy': 'top-heavy',
  corrections: 'corrections',
  'Form 5500 assembly': 'form-assembly',
  'EFAST2 submission': 'filing',
};

const COMPUTED_AGENT_TO_SLUG: Record<string, string> = {
  'Plan rules': 'plan-rules',
  Compensation: 'compensation',
  Reconciliation: 'reconciliation',
  Trust: 'trust',
  Corrections: 'corrections',
  Filing: 'form-assembly',
};

export function agentToStageSlug(agent: string): string | null {
  if (agent.startsWith('FTW · ')) {
    const label = agent.slice('FTW · '.length);
    return FTW_LABEL_TO_SLUG[label] ?? null;
  }
  return COMPUTED_AGENT_TO_SLUG[agent] ?? null;
}

const SLUG_TO_FTW_STAGE_KEY: Record<string, FtwStageKey> = {
  '410b-coverage': '410b_coverage',
  'adp-acp': 'adp_acp',
  '402g-limits': '402g_limits',
  '415-additions': '415_additions',
  'top-heavy': 'top_heavy',
  corrections: 'corrections',
  'form-assembly': 'form_assembly',
  filing: 'efast2_filing',
};

export function slugToFtwStageKey(slug: string): FtwStageKey | null {
  return SLUG_TO_FTW_STAGE_KEY[slug] ?? null;
}
